import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { ChangesHistoryService } from '../services/changes-history/changes-history.service';
import { HistoryPrepareService } from './tables/history-prepare.service';
import { AccessibilityLineChartService, AccessibilityLineChartServiceFactory } from '@client/app/blocks/service/charts/accessibility-line-chart/accessibility-line-chart.service';
import { ChangeHistoryLineChartService, ChangeHistoryLineChartServiceFactory } from '@client/app/blocks/service/charts/change-history-line-chart.service';
import { DataPrivacyLineChartService } from '@client/app/blocks/service/charts/data-privacy-line-chart/data-privacy-line-chart.service';
import { PerformanceDomainHistoryLineChartService } from '@client/app/blocks/service/charts/performance-domain-history-line-chart/performance-domain-history-line-chart.service';
import { PolicyLineChartService } from '@client/app/blocks/service/charts/policy-line-chart/policy-line-chart.service';
import { QALineChartService } from '@client/app/blocks/service/charts/qa-line-chart/qa-line-chart.service';
import { ReadabilityLineChartService } from '@client/app/blocks/service/charts/readability-line-chart/readability-line-chart.service';
import { SEOLineChartFactory } from '@client/app/blocks/service/charts/seo-line-chart/seo-line-chart.service';
import { CHART_COLORS_CONSTANT } from '@client/app/constants/chart-colors';
import { MonNumberService } from '@monsido/ng2/core/locale/services/mon-number.service';
import { ChartFactory } from '@client/app/blocks/service/charts/base-chart/chart-factory';
import { CrawlHistory } from '@monsido/ng2/modules/models/api/interfaces/domain.interface';

@Injectable({
    providedIn: 'root',
})
export class ChangesHistoryDashboardService {
    private history: any[] = [];
    private groupOptions: any[] = [];
    private lineChartService: ChartFactory | AccessibilityLineChartService | ChangeHistoryLineChartService | null = null;
    private domain: any = null;
    private activeModule = 'all';
    private groupOption: string | undefined = undefined;
    private date: { startDate: any; endDate: any } = {
        startDate: '',
        endDate: '',
    };
    private currentBrowserService: string | undefined;
    private historyBehaviorSubject = new BehaviorSubject<any[]>([]);

    private modulesScoresLabelTexts = {
        seo: this.translateService.getString('SEO score'),
        qa: this.translateService.getString('Quality Assurance score'),
        accessibility: this.translateService.getString('Accessibility score'),
    };

    private verticalScoresLabelTexts = {
        seo: this.translateService.getString('Industry average score'),
        qa: this.translateService.getString('Industry average score'),
        accessibility: this.translateService.getString('Industry average score'),
    };

    private scoresChartLineProperties = {
        yAxisID: 'y-axis-0',
        xAxisID: 'x-axis-0',
        type: 'line',
        fill: false,
        lineTension: 0,
        radius: 0,
        borderWidth: 1,
        pointRadius: 3,
        pointHitRadius: 34,
    };

    private scoresChartOptions = {
        layout: {
            padding: {
                bottom: 3,
                top: 3,
            },
        },
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                fontSize: 11,
                boxWidth: 10,
            },
        },
        scales: {
            yAxes: [
                {
                    id: 'y-axis-0',
                    type: 'linear',
                    position: 'left',
                    ticks: {
                        fontSize: 11,
                        min: 0,
                        beginAtZero: true,
                        suggestedMax: 5,
                        maxTicksLimit: 5,
                        callback: (value: number) => {
                            return this.monNumberService.transformNumber(value, '0,0') + '% ';
                        },
                    },
                    gridLines: {
                        drawBorder: false,
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.translateService.getString('Score'),
                        fontSize: 11,
                    },
                },
            ],
            xAxes: [
                {
                    id: 'x-axis-0',
                    type: 'time',
                    gridLines: {
                        offsetGridLines: true,
                        drawBorder: false,
                        display: false,
                    },
                    ticks: {
                        fontSize: 11,
                        maxTicksLimit: 30,
                    },
                },
            ],
        },
        tooltips: {
            intersect: false,
            mode: 'x-axis',
            callbacks: {
                label: (tooltipItem: any, data: any) => {
                    return (
                        data.datasets[tooltipItem.datasetIndex].label +
                        ': ' +
                        this.monNumberService.transformNumber(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].y / 100, '0.[00]%')
                    );
                },
                title: (tooltipItem: any) => {
                    return this.dateTimeService.format(tooltipItem[0].xLabel, 'MMM DD');
                },
            },
        },
    };

    public chartChangeObs = new BehaviorSubject<any>(null);
    public history$: Observable<any[]> = this.historyBehaviorSubject.asObservable();
    public pagesStack$: Observable<string[]> = this.changesHistoryService.pagesStackBehaviorSubject.asObservable();

    constructor (
        private monEventService: MonEventService,
        private seoLineChartFactory: SEOLineChartFactory,
        private accessibilityLineChartServiceFactory: AccessibilityLineChartServiceFactory,
        private qaLineChartService: QALineChartService,
        private policyLineChartService: PolicyLineChartService,
        private changeHistoryLineChartServiceFactory: ChangeHistoryLineChartServiceFactory,
        private readabilityLineChartService: ReadabilityLineChartService,
        private dataPrivacyLineChartService: DataPrivacyLineChartService,
        private performanceDomainHistoryLineChartService: PerformanceDomainHistoryLineChartService,
        private translateService: TranslateService,
        private dateTimeService: DateTimeService,
        private historyPrepareService: HistoryPrepareService,
        private changesHistoryService: ChangesHistoryService,
        private monNumberService: MonNumberService,
    ) {
        this.init();
    }

    private init (): void {
        this.setupGroupOptions();
        this.monEventService.addListener('hcSetHistory', (history: any) => {
            this.setHistory(history);
        });

        this.monEventService.addListener('hcSetDate', (date: any) => {
            this.setDate(date);
        });

        this.monEventService.addListener('hcSetDomain', (domain: any) => {
            this.setDomain(domain);
        });

        this.monEventService.addListener('hcSetModule', (module: string) => {
            this.setModule(module);
        });
    }

    getGroupOptions (): any[] {
        return this.groupOptions.filter(option => option.belongsTo === this.activeModule);
    }

    getDefaultGroupOption (): string | undefined {
        const groupOptions = this.getGroupOptions();
        if (groupOptions.length > 0) {
            const groupOption = groupOptions.find(option => option.value === this.groupOption);
            if (groupOption !== undefined) {
                return groupOption.value;
            }
            return groupOptions[0].value;
        }
        return undefined;
    }

    getHistory (): any[] {
        return this.history;
    }

    getOverride (): any {
        if (!this.lineChartService) {
            return {};
        }
        switch (this.activeModule) {
            case 'performance':
                return (this.lineChartService as unknown as PerformanceDomainHistoryLineChartService).getOverride(this.history);
            default:
                return this.lineChartService.getOverride();
        }
    }

    getSeries (): any[] {
        if (!this.lineChartService) {
            return [];
        }
        switch (this.activeModule) {
            case 'performance':
                return (this.lineChartService as unknown as PerformanceDomainHistoryLineChartService).getSeries(this.history);
            default:
                return this.lineChartService.getSeries();
        }
    }

    getColors (): string[] {
        if (!this.lineChartService) {
            return [];
        }
        switch (this.activeModule) {
            case 'performance':
                return (this.lineChartService as unknown as PerformanceDomainHistoryLineChartService).getColors(this.history);
            default:
                return this.lineChartService.getColors();
        }
    }

    getLabels (): any[] {
        if (!this.lineChartService || !this.dateTimeService.isValid(this.date.startDate) || !this.dateTimeService.isValid(this.date.endDate)) {
            return [];
        }

        const labels = [
            {
                date: this.date.startDate.startOf('day'),
            },
            {
                date: this.date.endDate.endOf('day'),
            },
        ];

        return this.lineChartService.getLabels(labels as unknown as CrawlHistory[]);
    }

    getData (): any[] {
        if (!this.lineChartService) {
            return [];
        }
        return this.lineChartService.getData(this.history);
    }

    getOptions (): Promise<any> {
        return new Promise((resolve) => {
            if (!this.lineChartService) {
                resolve({});
            } else {
                switch (this.activeModule) {
                    case 'accessibility':
                        (this.lineChartService as unknown as AccessibilityLineChartService).getOption('', this.history)
                            .then((options: any) => {
                                resolve(options);
                            })
                            .catch(() => {
                                resolve({});
                            });
                        break;
                    default:
                        resolve((this.lineChartService as ChangeHistoryLineChartService).getOption(this.history));
                        break;
                }
            }
        });
    }

    setGroupOption (group: string): void {
        if (group !== this.groupOption) {
            this.groupOption = group;
            this.setupChartData().then(() => {
                this.onChangeChart();
            }).catch(() => {});
        }
    }

    getScoresChartSeries (selectedModule: string): string[] {
        return [
            this.modulesScoresLabelTexts[selectedModule as keyof typeof this.modulesScoresLabelTexts],
            this.verticalScoresLabelTexts[selectedModule as keyof typeof this.verticalScoresLabelTexts],
        ];
    }

    getScoresChartColors (): string[] {
        return [CHART_COLORS_CONSTANT.LIGHT_BLUE, CHART_COLORS_CONSTANT.PEACH];
    }

    getScoresChartOverride (): any[] {
        return [this.scoresChartLineProperties, this.scoresChartLineProperties];
    }

    getScoresChartOptions (): any {
        return this.scoresChartOptions;
    }

    getProcessedScoresData (selectedModule: string, history: any[]): any[] {
        let processedHistory;
        switch (selectedModule) {
            case 'accessibility':
                processedHistory = this.historyPrepareService.prepareAccessibilityHistory(history);
                break;
            case 'qa':
                processedHistory = this.historyPrepareService.prepareQAHistory(history);
                break;
            case 'seo':
                processedHistory = this.historyPrepareService.prepareSEOHistory(history);
                break;
        }
        return processedHistory.map((item: any) => {
            return {
                x: item.post_processing_done_at,
                y: item.compliancePercentage,
            };
        });
    }

    getVerticalScores (selectedModule: string, history: any[]): any[] {
        return history.map((item: any) => {
            return {
                x: item.post_processing_done_at,
                y: item.vertical_scores && item.vertical_scores[selectedModule],
            };
        });
    }

    private setHistory (history: any[]): void {
        this.history = history;
        this.historyBehaviorSubject.next(history);
        this.currentBrowserService = this.history[0]?.js_rendering_service;
        const PUPPETEER_V20_UPDATE_TIME = new Date('2023-09-07T02:00:00+02:00').getTime();

        for (let i = 0; i < this.history.length; i++) {
            const entry = this.history[i];
            if (i > 0) {
                const currentEntryDate = new Date(entry.date).getTime();
                const prevEntryDate = new Date(this.history[i - 1].date).getTime();
                const comparison = currentEntryDate >= PUPPETEER_V20_UPDATE_TIME &&
                          PUPPETEER_V20_UPDATE_TIME > prevEntryDate;
                if (comparison) {
                    entry.isScanWithV20 = {
                        infoLink: 'https://help.monsido.com/en/articles/8288291-puppeteer-20-upgrade',
                    };
                }
            }
            if (this.currentBrowserService !== entry.js_rendering_service) {
                this.currentBrowserService = entry.js_rendering_service;
                entry.browserServiceChanged = {
                    value: true,
                    infoLink: 'https://help.monsido.com/en/articles/6341586-visual-indication-when-your-domain-is-being-scanned-with-new-browser-service',
                };
            }
        }

        this.setupChartData().then(() => {
            this.onChangeChart();
        }).catch(() => {});
    }

    private setDate (date: any): void {
        this.date = date;
    }

    private setDomain (domain: any): void {
        this.domain = domain;
        this.setHistory([]);
    }

    private setModule (module: string): void {
        this.activeModule = module;
        this.setupChartData().then(() => {
            this.onChangeChart();
        }).catch(() => {});
    }

    private setupChartData (): Promise<void> {
        return new Promise<void>((resolve) => {
            switch (this.activeModule) {
                case 'seo':
                    this.lineChartService = this.seoLineChartFactory.createSEOLineChart();
                    break;
                case 'accessibility':
                    this.lineChartService = this.accessibilityLineChartServiceFactory.createInstance();
                    this.lineChartService.init(this.domain, this.groupOption);
                    this.lineChartService.setup();
                    break;
                case 'qa':
                    this.lineChartService = this.qaLineChartService.createQaLineChartService();
                    break;
                case 'policy':
                    this.lineChartService = this.policyLineChartService.createPolicyLineChartService();
                    break;
                case 'readability':
                    this.lineChartService = this.readabilityLineChartService.createReadabilityLineChartService(this.domain);
                    break;
                case 'data-privacy':
                    this.lineChartService = this.dataPrivacyLineChartService.createPrivacyLineChartService();
                    break;
                case 'performance':
                    this.lineChartService = this.performanceDomainHistoryLineChartService.createPerformanceDomainHistoryLineChart();
                    break;
                default:
                    this.lineChartService = this.changeHistoryLineChartServiceFactory.createChangeHistoryLineChart();
                    this.lineChartService.init(this.domain);
                    break;
            }
            resolve();
        });
    }

    private onChangeChart (): void {
        this.chartChangeObs.next(null);
    }

    private setupGroupOptions (): void {
        this.groupOptions = [
            {
                name: this.translateService.getString('Issues'),
                value: 'issues',
                belongsTo: 'accessibility',
            },
            {
                name: this.translateService.getString('Checks'),
                value: null,
                belongsTo: 'accessibility',
            },
        ];
    }
}
